import React, { useCallback } from 'react';
import { Formik, Form } from 'formik';
import moment from 'moment';
import { PDFDocument } from 'pdf-lib';

import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import useHttpStatus from '~/commons/hooks/useHttpStatus';
import { initFormValues } from '~/commons/helpers';

import FormGroup from '~/commons/components/FormGroup.jsx';
import FormHttpAlert from '~/commons/components/FormHttpAlert.jsx';
import FormHttpSaveBtn from '~/commons/components/FormHttpSaveBtn.jsx';
import FieldFileSlider from '~/commons/components/FieldFileSlider.jsx';
import FieldArrayIterables from '~/commons/components/FieldArrayIterables.jsx';

import { env } from '../../config';

const { roles } = env.user;

/*
      if ( user.score >= 6000 ) {
        template = 'award_cert_22-23_edit_gold_filable.pdf';
      } else
      if ( user.score >= 5000 ) {
        template = 'award_cert_22-23_edit_silver_filable.pdf';
      } else
      if ( user.score >= 4000 ) {
        template = 'award_cert_22-23_edit_copper_filable.pdf';
      }

*/
const defaultCollapsible = {
  header: '',
  content: '',
};
const defaults = {
  goldScore: 6000,
  silverScore: 5000,
  copperScore: 4000,
  scoreCutOffDate: '',
  reviewOpenDate: '',
  reviewCloseDate: '',
  pdfOpenDate: '',
  pdfCloseDate: '',
  certificateDownloadStartDate: '',
  certificateDownloadEndDate: '',
  period1CertTemplateURL: '',
  period2CertTemplateURL: '',
  period3CertTemplateURL: '',
  finishedCertTemplateURL: '',
  readingMaterialPDFURL: '',
  teacherPDFURL: '',
  teacherPDFURL2: '',
  rankingSetting: {
    openDate: '',
    closeDate: '',
    closeImage: '',
  },
  exerciseSetting: {
    openDate: '',
    closeDate: '',
    closeImage: '',
  },
  ...(roles.includes('superAdmin') && {
    siteOpenDate: '',
    siteCloseDate: '',
    siteCloseImage: '',
    siteCloseLink: '',
    testIpsForClose: '',
    testIpsForOpen: '',
  }),
};

const FormGlobalSetting = ({ setting }) => {
  const [res, req] = useHttpStatus();
  const { t } = useTranslation();

  const validate = useCallback((f) => {
    const promise = new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(f);
      reader.addEventListener('load', (evt) => {
        const filecontent = evt.target.result;
        PDFDocument.load(filecontent)
          .then((pdfDoc) => {
            const form = pdfDoc.getForm();
            form.getTextField('School Name');
            form.getTextField('Student Name');
            resolve(true);
          })
          .catch(() => {
            reject(
              t(
                'PDF Template need form field [School Name] and [Student Name]',
              ),
            );
          });
      });
    });
    return promise;
  }, []);

  return (
    <Formik
      initialValues={initFormValues(
        defaults,
        _.merge({}, setting, {
          scoreCutOffDate: setting.scoreCutOffDate
            ? moment(setting.scoreCutOffDate).format('YYYY-MM-DD')
            : '',
          reviewOpenDate: setting.reviewOpenDate
            ? moment(setting.reviewOpenDate).format('YYYY-MM-DD')
            : '',
          reviewCloseDate: setting.reviewCloseDate
            ? moment(setting.reviewCloseDate).format('YYYY-MM-DD')
            : '',
          pdfOpenDate: setting.pdfOpenDate
            ? moment(setting.pdfOpenDate).format('YYYY-MM-DD')
            : '',
          pdfCloseDate: setting.pdfCloseDate
            ? moment(setting.pdfCloseDate).format('YYYY-MM-DD')
            : '',
          certificateDownloadStartDate: setting.certificateDownloadStartDate
            ? moment(setting.certificateDownloadStartDate).format('YYYY-MM-DD')
            : '',
          certificateDownloadEndDate: setting.certificateDownloadEndDate
            ? moment(setting.certificateDownloadEndDate).format('YYYY-MM-DD')
            : '',
          rankingSetting: {
            ...setting.rankingSetting,
            openDate: setting?.rankingSetting?.openDate
              ? {
                date: moment(setting.rankingSetting.openDate).format(
                  'YYYY-MM-DD',
                ),
                time: moment(setting.rankingSetting.openDate).format('HH:mm'),
              }
              : { date: '', time: '' },
            closeDate: setting?.rankingSetting?.closeDate
              ? {
                date: moment(setting.rankingSetting.closeDate).format(
                  'YYYY-MM-DD',
                ),
                time: moment(setting.rankingSetting.closeDate).format(
                  'HH:mm',
                ),
              }
              : { date: '', time: '' },
          },
          exerciseSetting: {
            ...setting.exerciseSetting,
            openDate: setting?.exerciseSetting?.openDate
              ? {
                date: moment(setting.exerciseSetting.openDate).format(
                  'YYYY-MM-DD',
                ),
                time: moment(setting.exerciseSetting.openDate).format(
                  'HH:mm',
                ),
              }
              : { date: '', time: '' },
            closeDate: setting?.exerciseSetting?.closeDate
              ? {
                date: moment(setting.exerciseSetting.closeDate).format(
                  'YYYY-MM-DD',
                ),
                time: moment(setting.exerciseSetting.closeDate).format(
                  'HH:mm',
                ),
              }
              : { date: '', time: '' },
          },
          ...(roles.includes('superAdmin') && {
            siteOpenDate: {
              date: setting.siteOpenDate
                ? moment(setting.siteOpenDate).format('YYYY-MM-DD')
                : '',
              time: setting.siteOpenDate
                ? moment(setting.siteOpenDate).format('HH:mm')
                : '',
            },
            siteCloseDate: {
              date: setting.siteCloseDate
                ? moment(setting.siteCloseDate).format('YYYY-MM-DD')
                : '',
              time: setting.siteCloseDate
                ? moment(setting.siteCloseDate).format('HH:mm')
                : '',
            },
          }),
        }),
      )}
      onSubmit={async (vs, actions) => {
        const values = {
          ...vs,
          scoreCutOffDate: vs.scoreCutOffDate
            ? moment(vs.scoreCutOffDate, 'YYYY-MM-DD').toDate()
            : null,
          reviewOpenDate: vs.reviewOpenDate
            ? moment(vs.reviewOpenDate, 'YYYY-MM-DD').toDate()
            : null,
          reviewCloseDate: vs.reviewCloseDate
            ? moment(vs.reviewCloseDate, 'YYYY-MM-DD').toDate()
            : null,
          certificateDownloadStartDate: vs.certificateDownloadStartDate
            ? moment(vs.certificateDownloadStartDate, 'YYYY-MM-DD').toDate()
            : null,
          certificateDownloadEndDate: vs.certificateDownloadEndDate
            ? moment(vs.certificateDownloadEndDate, 'YYYY-MM-DD').toDate()
            : null,
          ...(vs?.rankingSetting?.openDate
          || vs?.rankingSetting?.closeDate
          || vs?.rankingSetting?.closeImage
            ? {
              rankingSetting: {
                openDate: vs?.rankingSetting?.openDate
                  ? moment(
                    `${vs.rankingSetting.openDate.date} ${vs.rankingSetting.openDate.time}`,
                    'YYYY-MM-DD HH:mm',
                  ).toDate()
                  : null,
                closeDate: vs?.rankingSetting?.closeDate
                  ? moment(
                    `${vs.rankingSetting.closeDate.date} ${vs.rankingSetting.closeDate.time}`,
                    'YYYY-MM-DD HH:mm',
                  ).toDate()
                  : null,
                closeImage: vs?.rankingSetting?.closeImage,
              },
            }
            : {}),
          ...(vs?.exerciseSetting?.openDate || vs?.exerciseSetting?.closeDate
            ? {
              exerciseSetting: {
                openDate: vs?.exerciseSetting?.openDate
                  ? moment(
                    `${vs.exerciseSetting.openDate.date} ${vs.exerciseSetting.openDate.time}`,
                    'YYYY-MM-DD HH:mm',
                  ).toDate()
                  : null,
                closeDate: vs?.exerciseSetting?.closeDate
                  ? moment(
                    `${vs.exerciseSetting.closeDate.date} ${vs.exerciseSetting.closeDate.time}`,
                    'YYYY-MM-DD HH:mm',
                  ).toDate()
                  : null,
                closeImage: vs?.exerciseSetting?.closeImage,
              },
            }
            : {}),
          ...(roles.includes('superAdmin') && {
            siteOpenDate: vs.siteOpenDate
              ? moment(
                `${vs.siteOpenDate.date} ${vs.siteOpenDate.time}`,
                'YYYY-MM-DD HH:mm',
              ).toDate()
              : null,
            siteCloseDate: vs.siteCloseDate
              ? moment(
                `${vs.siteCloseDate.date} ${vs.siteCloseDate.time}`,
                'YYYY-MM-DD HH:mm',
              ).toDate()
              : null,
          }),
        };
        const { status } = await req({
          method: 'patch',
          url: `/api/site/setting/${setting._id}`,
          data: values,
        });

        if (status === 'success') {
          actions.setSubmitting(false);
          actions.resetForm({ values: vs });
        }
      }}
    >
      <Form>
        <FormHttpSaveBtn
          res={res}
          className="btn-primary mb-3"
          disabled={false}
        />
        <FormHttpAlert res={res} />
        {/* <div className="card my-3">
          <div className="card-header">{t('Article Random Setting')}</div>
          <div className="card-body">
            <div className="row">
              {_.map(env.subjects, (s, key) => (
                <div className="col-6 col-md-3" key={key}>
                  <FormGroup
                    name={`articleRandomSetting.${key}`}
                    label={s[env.language === 'en' ? 'en' : 'zhhk']}
                    type="number"
                    min={0}
                  />
                </div>
              ))}
            </div>
          </div>
        </div> */}

        <div className="card my-3">
          <div className="card-header">{t('Score Setting')}</div>
          <div className="card-body">
            <div className="row">
              <div className="col-12 col-md-4">
                <FormGroup
                  name="pdfOpenDate"
                  label={t('PDF Open Date')}
                  type="date"
                />
              </div>
              <div className="col-12 col-md-4">
                <FormGroup
                  name="pdfCloseDate"
                  label={t('PDF Close Date')}
                  type="date"
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-4">
                <FormGroup
                  name="reviewOpenDate"
                  label={t('Review Period Start Date')}
                  type="date"
                />
              </div>
              <div className="col-12 col-md-4">
                <FormGroup
                  name="reviewCloseDate"
                  label={t('Review Period End Date')}
                  type="date"
                />
              </div>
            </div>
            <FormGroup
              name="teacherPDFURL"
              as="upload"
              api="/api/site/view-slots/files"
              label={t('初小教師 PDF')}
              fileOpts={{ accept: 'application/pdf' }}
            />
            <FormGroup
              name="teacherPDFURL2"
              as="upload"
              api="/api/site/view-slots/files"
              label={t('高小教師 PDF')}
              fileOpts={{ accept: 'application/pdf' }}
            />
            <FormGroup
              name="readingMaterialPDFURL"
              as="upload"
              api="/api/site/view-slots/files"
              label={t('Review Period Reading Material PDF')}
              fileOpts={{ accept: 'application/pdf' }}
            />
            <div className="row">
              <div className="col">
                <div className="form-row">
                  <div className="col-12 col-md-4">
                    <FormGroup
                      name="scoreCutOffDate"
                      label={t('Score Cut Off Date')}
                      type="date"
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <FormGroup
                      name="certificateDownloadStartDate"
                      label={t('Certificate Download Start Date')}
                      type="date"
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <FormGroup
                      name="certificateDownloadEndDate"
                      label={t('Certificate Download End Date')}
                      type="date"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col">
                <div className="form-row">
                  <div className="col-12 col-md-4">
                    <FormGroup
                      name="goldScore"
                      label={t('Gold Score')}
                      type="number"
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <FormGroup
                      name="silverScore"
                      label={t('Silver Score')}
                      type="number"
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <FormGroup
                      name="copperScore"
                      label={t('Copper Score')}
                      type="number"
                    />
                  </div>
                </div>
              </div>
            </div>
            <FormGroup
              name="period1CertTemplateURL"
              as="upload"
              api="/api/site/view-slots/files"
              label={t('period1CertTemplate')}
              fileOpts={{ accept: 'application/pdf', validate }}
            />
            <FormGroup
              name="period2CertTemplateURL"
              as="upload"
              api="/api/site/view-slots/files"
              label={t('period2CertTemplate')}
              fileOpts={{ accept: 'application/pdf', validate }}
            />
            <FormGroup
              name="period3CertTemplateURL"
              as="upload"
              api="/api/site/view-slots/files"
              label={t('period3CertTemplate')}
              fileOpts={{ accept: 'application/pdf', validate }}
            />
            <FormGroup
              name="finishedCertTemplateURL"
              as="upload"
              api="/api/site/view-slots/files"
              label={t('finishedCertTemplate')}
              fileOpts={{ accept: 'application/pdf', validate }}
            />
          </div>
        </div>

        <div className="card my-3">
          <div className="card-header">{t('Ranking Setting')}</div>
          <div className="card-body">
            <div className="row">
              <div className="col">
                <FormGroup
                  name="rankingSetting.openDate.date"
                  label={t('Open Date')}
                  type="date"
                />
              </div>
              <div className="col">
                <FormGroup
                  name="rankingSetting.openDate.time"
                  label={t('Open Time')}
                  type="time"
                />
              </div>
              <div className="col">
                <FormGroup
                  name="rankingSetting.closeDate.date"
                  label={t('Close Date')}
                  type="date"
                />
              </div>
              <div className="col">
                <FormGroup
                  name="rankingSetting.closeDate.time"
                  label={t('Close Time')}
                  type="time"
                />
              </div>
            </div>
            <FormGroup
              name="rankingSetting.closeImage"
              as="coverImg"
              api="/api/site/view-slots/files"
              label={t('Close Image')}
            />
          </div>
        </div>

        <div className="card my-3">
          <div className="card-header">{t('Exercise Setting')}</div>
          <div className="card-body">
            <div className="row">
              <div className="col">
                <FormGroup
                  name="exerciseSetting.openDate.date"
                  label={t('Open Date')}
                  type="date"
                />
              </div>
              <div className="col">
                <FormGroup
                  name="exerciseSetting.openDate.time"
                  label={t('Open Time')}
                  type="time"
                />
              </div>
              <div className="col">
                <FormGroup
                  name="exerciseSetting.closeDate.date"
                  label={t('Close Date')}
                  type="date"
                />
              </div>
              <div className="col">
                <FormGroup
                  name="exerciseSetting.closeDate.time"
                  label={t('Close Time')}
                  type="time"
                />
              </div>
            </div>
            <FormGroup
              name="exerciseSetting.closeImage"
              as="coverImg"
              api="/api/site/view-slots/files"
              label={t('Close Image')}
            />
          </div>
        </div>

        {roles.includes('superAdmin') && (
          <div className="card my-3">
            <div className="card-header">{t('Site Open Close Setting')}</div>
            <div className="card-body">
              <div className="row">
                <div className="col">
                  <FormGroup
                    name="siteOpenDate.date"
                    label={t('Open Date')}
                    type="date"
                  />
                </div>
                <div className="col">
                  <FormGroup
                    name="siteOpenDate.time"
                    label={t('Open Time')}
                    type="time"
                  />
                </div>
                <div className="col">
                  <FormGroup
                    name="siteCloseDate.date"
                    label={t('Close Date')}
                    type="date"
                  />
                </div>
                <div className="col">
                  <FormGroup
                    name="siteCloseDate.time"
                    label={t('Close Time')}
                    type="time"
                  />
                </div>
              </div>

              <FormGroup
                name="siteCloseImage"
                as="coverImg"
                api="/api/site/view-slots/files"
                label={t('Site Close Image')}
              />
              <FormGroup name="siteCloseLink" label={t('Close Link')} />
              <FormGroup
                name="testIpsForClose"
                label={t('Test IP For Close')}
              />
              <FormGroup name="testIpsForOpen" label={t('Test IP For Open')} />
            </div>
          </div>
        )}
      </Form>
    </Formik>
  );
};

export default FormGlobalSetting;
